import { useNavigate, useSearchParams } from "react-router";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Building2, ChevronRight, Globe, History, UserRound } from "lucide-react";
import { PURPOSE_LABELS, SUBJECT_ROLE_LABELS, type SearchHistoryPage } from "../../shared/contracts.js";
import { api } from "../api.js";
import { useIsAdmin, useMe } from "../components/AppShell.js";
import { Badge } from "../components/ui/Badge.js";
import { Button } from "../components/ui/Button.js";
import { Card } from "../components/ui/Card.js";
import { EmptyState, ErrorState, LoadingBlock } from "../components/ui/States.js";
import { Table, TD, TH, THead, TR } from "../components/ui/Table.js";
import { TabPanel, Tabs } from "../components/ui/Tabs.js";
import { fmtDateTime, fmtInt, fmtRelative } from "../lib/format.js";
import { useDocumentTitle } from "../lib/hooks.js";
import { qk } from "../queries.js";

type Scope = "mine" | "institution";
type Item = SearchHistoryPage["items"][number];

export function HistoryPage() {
  useDocumentTitle("Historial");
  const me = useMe();
  const isAdmin = useIsAdmin();
  const [params, setParams] = useSearchParams();
  const scope: Scope = params.get("vista") === "institucion" ? "institution" : "mine";
  const navigate = useNavigate();

  const q = useInfiniteQuery({
    queryKey: qk.history(scope),
    queryFn: ({ pageParam, signal }) => api.searches.history(scope, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
  });
  const items = q.data?.pages.flatMap((p) => p.items) ?? [];
  const open = (id: string) => void navigate(`/buscar?s=${encodeURIComponent(id)}`);
  const nextPageError = q.isFetchNextPageError ? q.error : null;

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-xl font-semibold text-fg">Historial de búsquedas</h1>
        <p className="text-muted">
          Cada búsqueda queda registrada con su finalidad y n° de caso. Ábrela para volver a ver los resultados (se
          recalculan con el índice actual).
        </p>
      </div>
      <Card className="overflow-hidden">
        <Tabs
          idPrefix="hist"
          label="Alcance del historial"
          className="px-2"
          value={scope}
          onChange={(s) => setParams(s === "institution" ? { vista: "institucion" } : {}, { replace: true })}
          items={[
            { value: "mine", label: "Mis búsquedas", icon: <UserRound aria-hidden="true" /> },
            {
              value: "institution",
              label: isAdmin && !me.institution ? "Todas las instituciones" : "Mi institución",
              icon: <Building2 aria-hidden="true" />,
            },
          ]}
        />
        <TabPanel idPrefix="hist" value={scope}>
          {q.isPending ? (
            <LoadingBlock />
          ) : q.isError && !q.data ? (
            <ErrorState error={q.error} onRetry={() => void q.refetch()} className="m-4" />
          ) : items.length === 0 ? (
            <EmptyState
              icon={<History />}
              title="Aún no hay búsquedas"
              description={scope === "mine" ? "Tus búsquedas aparecerán aquí." : "Las búsquedas de tu institución aparecerán aquí."}
            />
          ) : (
            <>
              <div className="hidden md:block">
                <HistoryTable items={items} showUser={scope === "institution"} onOpen={open} />
              </div>
              <ul className="divide-y divide-border md:hidden">
                {items.map((it) => (
                  <li key={it.id}>
                    <button type="button" onClick={() => open(it.id)} className="flex w-full items-start gap-3 px-4 py-3 text-left hover:bg-surface-2">
                      <div className="min-w-0 flex-1 space-y-1">
                        <p className="truncate font-medium text-fg">{it.queryText}</p>
                        <p className="text-[12.5px] text-muted">
                          {fmtDateTime(it.createdAt)} · {fmtInt(it.resultCount)} resultados
                          {scope === "institution" ? ` · ${it.user.fullName}` : ""}
                        </p>
                        <PurposeBadges item={it} />
                      </div>
                      <ChevronRight className="mt-1 size-4 text-subtle" aria-hidden="true" />
                    </button>
                  </li>
                ))}
              </ul>
              {(q.hasNextPage || nextPageError) && (
                <div className="flex flex-col items-center gap-2 border-t border-border py-3">
                  {nextPageError !== null && (
                    <p role="alert" className="text-[13px] text-danger">
                      No se pudieron cargar más búsquedas.
                    </p>
                  )}
                  <Button size="sm" onClick={() => void q.fetchNextPage()} loading={q.isFetchingNextPage}>
                    Cargar más
                  </Button>
                </div>
              )}
            </>
          )}
        </TabPanel>
      </Card>
    </div>
  );
}

function PurposeBadges({ item }: { item: Item }) {
  return (
    <span className="inline-flex flex-wrap gap-1">
      <Badge tone="neutral" title={item.purposeDetail ?? undefined}>
        {PURPOSE_LABELS[item.purpose]}
      </Badge>
      {item.subjectRole && <Badge tone="outline">{SUBJECT_ROLE_LABELS[item.subjectRole]}</Badge>}
      {item.liveRequested && (
        <Badge tone="accent" icon={<Globe />}>
          Internet
        </Badge>
      )}
    </span>
  );
}

function HistoryTable({ items, showUser, onOpen }: { items: Item[]; showUser: boolean; onOpen: (id: string) => void }) {
  return (
    <Table caption="Búsquedas realizadas">
      <THead>
        <tr>
          <TH>Fecha</TH>
          <TH>Búsqueda</TH>
          <TH>Finalidad</TH>
          <TH>Caso</TH>
          <TH className="text-right">Resultados</TH>
          {showUser && <TH>Usuario</TH>}
          <TH>
            <span className="sr-only">Acciones</span>
          </TH>
        </tr>
      </THead>
      <tbody>
        {items.map((it) => (
          <TR key={it.id} className="cursor-pointer hover:bg-surface-2" onClick={() => onOpen(it.id)}>
            <TD className="whitespace-nowrap">
              <span className="text-fg">{fmtDateTime(it.createdAt)}</span>
              <span className="block text-[12px] text-subtle">{fmtRelative(it.createdAt)}</span>
            </TD>
            <TD className="max-w-[22rem]">
              <span className="line-clamp-2 font-medium break-words text-fg">{it.queryText}</span>
            </TD>
            <TD>
              <PurposeBadges item={it} />
              {it.purposeDetail && <span className="mt-0.5 block max-w-56 truncate text-[12px] text-subtle">{it.purposeDetail}</span>}
            </TD>
            <TD className="font-mono text-[12.5px]">{it.caseRef ?? <span className="text-subtle">—</span>}</TD>
            <TD className="text-right tabular-nums">{fmtInt(it.resultCount)}</TD>
            {showUser && (
              <TD>
                <span className="text-fg">{it.user.fullName}</span>
                <span className="block text-[12px] text-subtle">{it.user.email}</span>
              </TD>
            )}
            <TD className="text-right">
              <Button
                size="sm"
                variant="ghost"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpen(it.id);
                }}
                aria-label={`Abrir la búsqueda del ${fmtDateTime(it.createdAt)}`}
              >
                Abrir
                <ChevronRight className="size-4" aria-hidden="true" />
              </Button>
            </TD>
          </TR>
        ))}
      </tbody>
    </Table>
  );
}
